import { useCallback, useEffect, useMemo, useState } from 'react';
import config from '../config';
import { normaliserObjectifs } from '../utils/objectifsCampagne';

const API_URL = config.API_URL;

/** Nom de l'événement qui prévient les autres instances qu'une écriture a eu lieu. */
const EVENEMENT_MAJ = 'kokpeet:campagnes-majournees';

/**
 * Les labels et objectifs des campagnes d'un compte — une seule source pour tout l'écran.
 *
 * ## Ce qu'il remplace
 *
 * Le chargement de `GET /api/campaigns` était recopié trois fois dans `App.js` sous trois jeux de
 * noms (`campaignLabelsMap` côté Google, `campaignsWithLabels` côté Meta, `msCampaignsWithLabels`
 * côté Microsoft), et le calcul des identifiants filtrés y était réécrit huit fois. Le panneau
 * d'édition et le filtre par label ont besoin du **même** état : sans ça, poser un label
 * n'apparaîtrait dans le filtre qu'au rechargement de la page.
 *
 * ## Comment le rafraîchissement se propage
 *
 * Ce projet n'a aucun contexte React ni store : l'idiome maison est un `CustomEvent` sur `window`.
 * Toute écriture émet donc `kokpeet:campagnes-majournees`, et **le hook écoute lui-même cet
 * événement** : chaque autre instance montée sur le même compte se recharge. L'écouteur est
 * interne, donc invisible pour les appelants — le panneau vit dans le tableau des campagnes, le
 * filtre vit dans le dashboard, et ni l'un ni l'autre n'a à connaître l'existence de l'autre.
 *
 * @param {Object}  options
 * @param {string}  options.accountId    `sourceId` du compte (les tirets sont retirés ici)
 * @param {string}  options.accountType  Type de `LibraryItem`, pour ne lister que cette régie
 * @param {string}  options.token        Jeton ; repli sur `localStorage` si absent
 * @param {boolean} options.actif        `false` n'émet aucune requête (panneau fermé)
 */
export function useCampagnesEtiquetees({ accountId, accountType, token, actif = true } = {}) {
  const [campagnes, setCampagnes] = useState([]);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);

  const compteNormalise = accountId ? String(accountId).replace(/-/g, '') : null;

  /**
   * Le jeton n'est pas toujours disponible en prop.
   *
   * `CampaignsTable` n'en reçoit aucun, et l'ajouter imposerait une prop de plus sur ses six
   * points de montage. Le repli sur `localStorage` reste l'exception, pas la règle : un appelant
   * qui a le jeton doit le passer.
   */
  const jeton = token || (typeof window !== 'undefined' ? window.localStorage.getItem('kokpeet_token') : null);

  const enTetes = useMemo(() => ({
    Authorization: `Bearer ${jeton}`,
    'Content-Type': 'application/json'
  }), [jeton]);

  const charger = useCallback(async () => {
    if (!actif || !compteNormalise || !jeton) return;

    setChargement(true);
    setErreur(null);
    try {
      const parametres = new URLSearchParams({ accountId: compteNormalise });
      if (accountType) parametres.set('accountType', accountType);

      const reponse = await fetch(`${API_URL}/api/campaigns?${parametres}`, { headers: enTetes });
      const donnees = await reponse.json();

      if (!donnees.success) {
        setErreur(donnees.error || 'Chargement des campagnes impossible');
        return;
      }
      setCampagnes(donnees.campaigns || []);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setChargement(false);
    }
  }, [actif, compteNormalise, accountType, jeton, enTetes]);

  useEffect(() => { charger(); }, [charger]);

  // Une écriture faite ailleurs sur le même compte doit se voir ici aussi
  useEffect(() => {
    if (!compteNormalise) return;
    const surMaj = (evenement) => {
      if (evenement.detail?.accountId === compteNormalise) charger();
    };
    window.addEventListener(EVENEMENT_MAJ, surMaj);
    return () => window.removeEventListener(EVENEMENT_MAJ, surMaj);
  }, [compteNormalise, charger]);

  const prevenirLesAutres = useCallback(() => {
    window.dispatchEvent(new CustomEvent(EVENEMENT_MAJ, { detail: { accountId: compteNormalise } }));
  }, [compteNormalise]);

  /**
   * Écritures. Chacune recharge derrière elle plutôt que de rapiécer l'état local : le serveur
   * normalise les labels (`trim().toLowerCase()`) et peut donc rendre autre chose que ce qu'on
   * lui a envoyé.
   */
  const ecrire = useCallback(async (url, options) => {
    const reponse = await fetch(url, { headers: enTetes, ...options });
    const donnees = await reponse.json().catch(() => ({}));
    if (!reponse.ok || !donnees.success) {
      throw new Error(donnees.error || `Échec de l'enregistrement (${reponse.status})`);
    }
    await charger();
    prevenirLesAutres();
    return donnees;
  }, [enTetes, charger, prevenirLesAutres]);

  const enregistrerLabels = useCallback((campaignId, labels) => ecrire(
    `${API_URL}/api/campaigns/${campaignId}/labels`,
    { method: 'PUT', body: JSON.stringify({ accountId: compteNormalise, accountType, labels }) }
  ), [ecrire, compteNormalise, accountType]);

  const enregistrerObjectif = useCallback((campaignId, objectiveType, objectiveValue) => ecrire(
    `${API_URL}/api/tools/campaign-objectives`,
    {
      method: 'POST',
      body: JSON.stringify({ accountId: compteNormalise, campaignId, objectiveType, objectiveValue })
    }
  ), [ecrire, compteNormalise]);

  const supprimerObjectif = useCallback((campaignId, objectiveType) => ecrire(
    `${API_URL}/api/tools/campaign-objectives/${campaignId}/${objectiveType}?accountId=${compteNormalise}`,
    { method: 'DELETE' }
  ), [ecrire, compteNormalise]);

  const labelsParCampagne = useMemo(() => {
    const parCampagne = {};
    campagnes.forEach(c => { parCampagne[c.campaignId] = c.labels || []; });
    return parCampagne;
  }, [campagnes]);

  const labelsDisponibles = useMemo(() => {
    const tous = new Set();
    campagnes.forEach(c => (c.labels || []).forEach(l => tous.add(l)));
    return Array.from(tous).sort();
  }, [campagnes]);

  const objectifsParCampagne = useMemo(() => {
    const parCampagne = {};
    campagnes.forEach(c => { parCampagne[c.campaignId] = c.objectifs || []; });
    return normaliserObjectifs(parCampagne);
  }, [campagnes]);

  return {
    campagnes,
    labelsParCampagne,
    labelsDisponibles,
    objectifsParCampagne,
    chargement,
    erreur,
    recharger: charger,
    enregistrerLabels,
    enregistrerObjectif,
    supprimerObjectif
  };
}

/**
 * Les identifiants des campagnes portant l'un des labels sélectionnés.
 *
 * Fonction de module, et non valeur du hook : la **sélection** de labels appartient à chaque
 * dashboard, qui a déjà son `selectedLabelsFilter`. Le hook ne porte que les données du serveur.
 *
 * ⚠️ **`null` et `[]` ne veulent pas dire la même chose.** `null` signifie « aucun filtre », `[]`
 * signifie « un filtre qui ne retient rien ». Les deux se confondaient : côté Meta, un label sans
 * correspondance rendait `null`, et le tableau affichait donc **toutes** les campagnes du compte
 * au lieu d'aucune. Le backend lisant partout `length > 0`, l'erreur était invisible.
 */
export function idsCampagnesPourLabels(labelsParCampagne, labelsSelectionnes) {
  if (!labelsSelectionnes || labelsSelectionnes.length === 0) return null;

  return Object.entries(labelsParCampagne || {})
    .filter(([, labels]) => labelsSelectionnes.some(choisi => (labels || []).includes(choisi)))
    .map(([campaignId]) => campaignId);
}

export default useCampagnesEtiquetees;
